type QueueEntry<T> = {
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
};

export class RequestThrottle {
  private active = 0;
  private readonly maxConcurrent: number;
  private queue: QueueEntry<unknown>[] = [];

  constructor(maxConcurrent = 3) {
    this.maxConcurrent = maxConcurrent;
  }

  async run<T>(fn: () => Promise<T>): Promise<T> {
    if (this.active < this.maxConcurrent) {
      return this.execute(fn);
    }

    return new Promise<T>((resolve, reject) => {
      // LIFO: push to front so newest request gets served first
      this.queue.unshift({ execute: fn, resolve, reject } as QueueEntry<unknown>);

      // Drop stale requests beyond a reasonable queue depth
      if (this.queue.length > 6) {
        const dropped = this.queue.pop();
        dropped?.reject(new Error('Request dropped: newer request queued'));
      }
    });
  }

  private async execute<T>(fn: () => Promise<T>): Promise<T> {
    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      this.dequeue();
    }
  }

  private dequeue() {
    if (this.queue.length === 0 || this.active >= this.maxConcurrent) return;
    const next = this.queue.shift();
    if (next) {
      this.execute(next.execute).then(next.resolve, next.reject);
    }
  }
}

export const hyperliquidThrottle = new RequestThrottle(3);
